import React, { useState, useRef, useEffect } from 'react'
import { Clock, ChevronDown } from 'lucide-react'

interface TimeSelectorProps {
  selectedDay: string
  day: string
  availability: Record<string, { timings: any[] }>
  currentTiming: { start: string; end: string }
  handleTimingChange: (type: 'start' | 'end', value: string) => void
  handleAddTiming: () => void
  hours: Record<string, string>
  t: (key: string) => string
}

const TimeSelector: React.FC<TimeSelectorProps> = ({
  selectedDay,
  day,
  availability,
  currentTiming,
  handleTimingChange,
  handleAddTiming,
  hours,
  t,
}) => {
  const [openDropdown, setOpenDropdown] = useState<'start' | 'end' | null>(null)
  const startRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node
    if (
      openDropdown === 'start' &&
      startRef.current &&
      !startRef.current.contains(target)
    ) {
      setOpenDropdown(null)
    }
    if (
      openDropdown === 'end' &&
      endRef.current &&
      !endRef.current.contains(target)
    ) {
      setOpenDropdown(null)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openDropdown])

  if (selectedDay !== day || availability[day]?.timings.length > 0) return null

  return (
    <div className='w-full  flex flex-col sm:flex-row gap-4 items-end'>
      <div className='w-full'>
        <div className='relative' ref={startRef}>
          <Clock className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
          <CustomSelect
            type='start'
            value={currentTiming.start}
            placeholder={t('create-ad.from')}
            hours={hours}
            handleTimingChange={handleTimingChange}
          />
        </div>
      </div>

      <div className='w-full'>
        <div className='relative' ref={endRef}>
          <Clock className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
          <CustomSelect
            type='end'
            value={currentTiming.end}
            placeholder={t('create-ad.to')}
            hours={hours}
            handleTimingChange={handleTimingChange}
          />
        </div>
      </div>

      <button
        disabled={!currentTiming.start || !currentTiming.end}
        onClick={handleAddTiming}
        className={`w-full px-6 py-2 rounded-lg font-medium ${
          !currentTiming.start || !currentTiming.end
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-redpink text-white hover:bg-redpink/90 active:scale-95'
        }`}
      >
        {t('add')}
      </button>
    </div>
  )
}

interface CustomSelectProps {
  hours: Record<string, string>
  handleTimingChange: (type: 'start' | 'end', value: string) => void

  type: 'start' | 'end'
  value: string
  placeholder: string
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  type,
  value,
  placeholder,
  hours,
  handleTimingChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className='relative w-full'>
      <div
        className='w-full pl-7 pr-1 py-2 bg-white border border-gray-200 rounded-lg cursor-pointer flex justify-between items-center'
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className={value ? 'text-gray-700' : 'text-gray-400'}>
          {value ? hours[value] : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      {isOpen && (
        <div
          className='absolute z-50 mt-1 bg-white border rounded-lg shadow-lg max-h-[300px] overflow-y-auto'
          style={{ width: '280px', left: 'calc(50% - 140px)' }}
        >
          <div className='grid grid-cols-4 gap-0.5 p-2'>
            {Object.entries(hours).map(([hour, label]) => (
              <div
                key={hour}
                className={`px-2 py-2 cursor-pointer text-center text-sm rounded-md ${
                  value === hour
                    ? 'bg-redpink/10 text-redpink font-medium'
                    : 'text-gray-700 hover:bg-redpink/10 hover:text-redpink'
                }`}
                onClick={() => {
                  handleTimingChange(type, hour)
                  setIsOpen(false)
                }}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TimeSelector
